<script setup lang="ts">
import MainBreadcrumbs from "@/ui/components/header/MainBreadcrumbs.vue";
import { ref, watch, onMounted } from "vue";
import { t } from "@/plugins/i18n";
import { useApiClient } from "@/api/client";
import useDialog from "@/ui/composables/useDialog";
import router from "@/router/router";
import { useDimensionValuesStore } from "@/modules/dimension_values/store";

type DimensionDO = App.Data.DimensionDO;

// ============================================================================

const client = useApiClient("dimensions");
const dialog = useDialog();
const store = useDimensionValuesStore();

const breadcrumbs = [
	{
		title: "Dimension Values",
		disabled: true,
		to: "/dimension-values",
	},
];

type TabsType = {
	name_clean: string;
	icon: string;
	visible: boolean;
};

type DimensionWithName = DimensionDO & { name: string };

const tabs: TabsType[] = [
	{
		name_clean: "nominal",
		icon: "fa-sliders",
		visible: true,
	},
	{
		name_clean: "bucketed",
		icon: "fa-bucket",
		visible: true,
	},
];
const headers = [
	{
		title: "Title",
		key: "name",
		sortable: true,
	},
];

const loading = ref(false);
const dimensions = ref<DimensionWithName[]>([]);

const itemsPerPage = ref(100);
const totalItemCount = ref(0);
const search = ref("");

async function fetchValues() {
	loading.value = true;

	const response = await client.get({
		"filter[type]": store.valueType,
	});

	if (response.isOk()) {
		dimensions.value = response.value.map((d) => ({
			name: t(`properties.${d.name_clean}.title`),
			...d,
		}));
		loading.value = false;
	}

	if (response.isErr()) {
		await dialog.alert({
			title: "Something went wrong...",
			message: `${response.error.status}: ${response.error.message}`,
		});
	}
}

watch(
	() => store.valueType,
	async () => await fetchValues()
);
onMounted(async () => await fetchValues());

async function navigateToDimension(name_clean: string, id: number) {
	await router.push({
		name: `dimensions.${store.valueType}`,
		params: { name_clean, id },
	});
}
</script>

<template>
	<main-breadcrumbs :breadcrumbs="breadcrumbs" />

	<v-card>
		<v-tabs v-model="store.valueType">
			<v-tab
				v-for="tab in tabs"
				v-show="tab.visible"
				:key="tab.name_clean"
				:value="tab.name_clean"
			>
				<i :class="`fa-duotone ${tab.icon} fa-lg`"></i>
				<div class="tab-text">
					{{ t("headers." + tab.name_clean) }}
				</div>
			</v-tab>
		</v-tabs>
		<div class="px-4 pt-4 pb-2">
			<v-text-field
				v-model="search"
				label="Search"
				prepend-inner-icon="mdi-magnify"
				variant="filled"
				hide-details
				single-line
			/>
			<v-data-table
				v-model:items-per-page="itemsPerPage"
				:search="search"
				:sort-by="[{ key: 'name', order: 'asc' }]"
				:headers="headers"
				:items="dimensions"
				:items-length="totalItemCount"
				:loading="loading"
				density="comfortable"
				class="mt-3"
			>
				<template v-slot:item.name="{ item }">
					<span
						class="text-decoration-underline cursor-pointer"
						@click="navigateToDimension(item.name_clean, item.id)"
					>
						{{ item.name }}
					</span>
				</template>
			</v-data-table>
		</div>
	</v-card>
</template>
